import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Wrench, Handshake, Building, LogOut, X, User, Map,
  ChevronsLeft, ChevronsRight, FileText, BarChart2, FileUp, List, RefreshCcw,
  ChevronDown, ChevronRight
} from 'lucide-react';

const Sidebar = ({ sidebarOpen, setSidebarOpen, sidebarCollapsed, onToggleCollapse }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [commissionsDropdownOpen, setCommissionsDropdownOpen] = useState(false);
  const [corporateDropdownOpen, setCorporateDropdownOpen] = useState(false);

  if (!user) {
    return null;
  }

  const navLinks = [
    // { name: 'Dashboard', path: '/', icon: Building, dept: ['comercial', 'diretoria'] },
    { name: 'Dashboard Comercial', path: '/commercial-dashboard', icon: BarChart2, dept: ['comercial' , 'diretoria'], excludePosition: ['assistente comercial'] },
    { name: 'Ferramentas', path: '/tools', icon: Wrench, dept: ['comercial', 'diretoria'], position: ['assistente', 'assistente comercial'] },
    { name: 'Parceiros', path: '/partners', icon: Handshake, dept: ['comercial', 'diretoria'] , position: ['assistente', 'assistente comercial']},
    { name: 'Solicitações', path: '/solicitations', icon: FileText, dept: ['comercial', 'diretoria'], position: ['assistente', 'assistente comercial']},
    { name: 'Rotas', path: '/rotas', icon: Map, dept: ['comercial', 'diretoria'], position: ['assistente', 'assistente comercial']},
    { name: 'Administração', path: '/admin', icon: User, dept: ['administrativo'] },
    // Menu Corporativo
    { type: 'title', name: 'Corporativo', icon: Building, dept: ['corporativo'] },
    { name: 'Upload de Relatório', path: '/corporate/upload', icon: FileUp, dept: ['corporativo'] },
    { name: 'Ver Relatórios', path: '/corporate/reports', icon: List, dept: ['corporativo'] },
    // Menu Comissões
    { type: 'title', name: 'Comissões', icon: FileText, dept: ['financeiro'] },
    { name: 'Upload de Relatório', path: '/comissions/upload', icon: FileUp, dept: ['financeiro'] },
    { name: 'Ver Relatórios', path: '/comissions/list', icon: List, dept: ['financeiro'] },
    { name: 'Fila de Correção', path: '/comissions/correction-queue', icon: RefreshCcw, dept: ['financeiro'] },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userPosition = user?.position?.toLowerCase();
  const isSuperAdmin = userPosition === 'administrador' || userPosition === 'diretor';

  const NavLink = ({ link, nested = false }) => {
    const isActive = link.path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(link.path);

    return (
      <Link
        to={link.path}
        onClick={() => setSidebarOpen(false)}
        title={sidebarCollapsed ? link.name : ''}
        className={`w-full flex items-center h-12 px-4 text-sm font-medium transition-colors duration-200 group ${
          isActive ? 'bg-primary-dark text-white' : 'text-gray-300 hover:bg-primary-dark hover:text-white'
        } ${sidebarCollapsed ? 'justify-center' : ''} ${nested ? 'pl-8' : ''}`}
      >
        {link.icon && <link.icon className="w-5 h-5 flex-shrink-0" />}
        <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-0 ml-0' : 'w-auto ml-3'}`}>
          {link.name}
        </span>
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-center h-16 px-4 border-b border-primary-dark flex-shrink-0 relative">
        <Link to="/" className={`text-2xl font-bold text-white cursor-pointer transition-all duration-300 whitespace-nowrap ${sidebarCollapsed ? 'opacity-0 scale-50' : 'opacity-100 scale-100'}`}>TN-Admin</Link>
        <Link to="/" className={`absolute text-2xl font-bold text-white cursor-pointer transition-all duration-300 whitespace-nowrap ${sidebarCollapsed ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>TN</Link>
        <button onClick={() => setSidebarOpen(false)} className="absolute right-4 md:hidden text-gray-300 hover:text-white"><X size={24} /></button>
      </div>
      <nav className="flex-grow mt-5 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {navLinks.map((link) => {
          const deptMatch = link.dept && user.department && link.dept.includes(user.department.toLowerCase());
          const posMatch = link.position && userPosition && link.position.includes(userPosition);
          const excludedMatch = link.excludePosition && userPosition && link.excludePosition.includes(userPosition);

          if (!isSuperAdmin) {
            if (excludedMatch) return null;
            if ((link.dept || link.position) && !deptMatch && !posMatch) {
              return null;
            }
          }

          // Adicionando a lógica para o dropdown de comissões
          if (link.type === 'title' && link.name === 'Comissões') {
            return (
              <div key={link.name}>
                <div
                  onClick={() => setCommissionsDropdownOpen(!commissionsDropdownOpen)}
                  className={`w-full flex items-center h-12 px-4 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                    commissionsDropdownOpen || location.pathname.startsWith('/comissions')
                      ? 'bg-primary-dark text-white'
                      : 'text-gray-300 hover:bg-primary-dark hover:text-white'
                  } ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}
                  title={sidebarCollapsed ? link.name : ''}
                >
                  <div className={`flex items-center ${sidebarCollapsed ? 'mx-auto' : ''}`}>
                    {link.icon && <link.icon className="w-5 h-5 flex-shrink-0" />}
                    <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-0 ml-0' : 'w-auto ml-3'}`}>
                      {link.name}
                    </span>
                  </div>
                  {!sidebarCollapsed && (
                    commissionsDropdownOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  )}
                </div>
                {/* Renderiza os links de comissão apenas se o dropdown estiver aberto */}
                {!sidebarCollapsed && commissionsDropdownOpen && (
                  navLinks
                    .filter(subLink => subLink.type !== 'title' && subLink.path?.startsWith('/comissions'))
                    .map(subLink => <NavLink key={subLink.name} link={subLink} nested={true} />)
                )}
              </div>
            );
          }

          // Adicionando a lógica para o dropdown do Corporativo
          if (link.type === 'title' && link.name === 'Corporativo') {
            return (
              <div key={link.name}>
                <div
                  onClick={() => setCorporateDropdownOpen(!corporateDropdownOpen)}
                  className={`w-full flex items-center h-12 px-4 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                    corporateDropdownOpen || location.pathname.startsWith('/corporate')
                      ? 'bg-primary-dark text-white'
                      : 'text-gray-300 hover:bg-primary-dark hover:text-white'
                  } ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}
                  title={sidebarCollapsed ? link.name : ''}
                >
                  <div className={`flex items-center ${sidebarCollapsed ? 'mx-auto' : ''}`}>
                    {link.icon && <link.icon className="w-5 h-5 flex-shrink-0" />}
                    <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-0 ml-0' : 'w-auto ml-3'}`}>
                      {link.name}
                    </span>
                  </div>
                  {!sidebarCollapsed && (
                    corporateDropdownOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  )}
                </div>
                {!sidebarCollapsed && corporateDropdownOpen && (
                  navLinks
                    .filter(subLink => subLink.type !== 'title' && subLink.path?.startsWith('/corporate'))
                    .map(subLink => <NavLink key={subLink.name} link={subLink} nested={true} />)
                )}
              </div>
            );
          }

          // Se for um sublink de comissões, não renderizamos aqui pois já é tratado acima
          if ((link.path?.startsWith('/comissions') || link.path?.startsWith('/corporate')) && link.type !== 'title') {
            return null;
          }

          return <NavLink key={link.name} link={link} />;
        })}
      </nav>
      <div className="flex-shrink-0">
        <div className="hidden md:block p-2 border-t border-primary-dark">
          <button onClick={onToggleCollapse} className="w-full flex items-center h-10 px-4 text-sm font-medium text-gray-300 hover:bg-primary-dark hover:text-white rounded-md transition-colors group">
            <div className={`flex items-center ${sidebarCollapsed ? 'mx-auto' : ''}`}>
              {sidebarCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
              <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-0 ml-0' : 'w-auto ml-3'}`}>Recolher</span>
            </div>
          </button>
        </div>
        <div className="p-4 border-t border-primary-dark">
          <div className={`flex items-center w-full`}>
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-dark flex items-center justify-center"><User className="text-white" /></div>
            <div className={`ml-3 whitespace-nowrap overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-0' : 'w-auto'}`}><p className="text-sm font-medium text-white truncate">{user.first_name || user.name || 'Usuário'}</p></div>
          </div>
          <button onClick={handleLogout} title="Sair" className={`w-full flex items-center mt-4 h-10 px-4 text-sm font-medium text-gray-300 hover:bg-primary-dark hover:text-white rounded-md transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}>
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-0 ml-0' : 'w-auto ml-3'}`}>Sair</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden transition-opacity ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setSidebarOpen(false)} />
      <aside className={`fixed inset-y-0 left-0 bg-primary transform transition-all duration-300 ease-in-out z-30 md:relative md:translate-x-0 md:flex-shrink-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarCollapsed ? 'md:w-20' : 'md:w-64'}`}>
        <SidebarContent />
      </aside>
    </>
  );
};
export default Sidebar;